import { lazy, Suspense, useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom'
import './tokens/theme.css'
import { registerDevice, pingDevice } from './lib/device-id.js'
import { BottomNav } from './components/bottom-nav.jsx'
import { ErrorBoundary } from './components/error-boundary.jsx'
import { Paywall } from './components/paywall.jsx'

// Lazy load pages for code splitting. Importers are exported so other
// routes (notably Home) can call them to warm chunks ahead of navigation
// — e.g. preloading StoryPlayer while the user is still browsing the
// feed, so tap-to-play never waits on a JS round-trip.
const importHome = () => import('./pages/Home.jsx')
const importExplore = () => import('./pages/Explore.jsx')
const importSettings = () => import('./pages/Settings.jsx')
const importStoryPlayer = () => import('./pages/StoryPlayer.jsx')
const importStore = () => import('./pages/Store.jsx')
const importStreaks = () => import('./pages/Streaks.jsx')
const importStickers = () => import('./pages/Stickers.jsx')
const importProfileLook = () => import('./pages/ProfileLook.jsx')
const importProfileSettings = () => import('./pages/ProfileSettings.jsx')
const importNotifications = () => import('./pages/Notifications.jsx')
const importPreferences = () => import('./pages/Preferences.jsx')
const importLegal = () => import('./pages/Legal.jsx')
const importPrivacy = () => import('./pages/Privacy.jsx')
const importTerms = () => import('./pages/Terms.jsx')
const importChildrenPrivacy = () => import('./pages/ChildrenPrivacy.jsx')

export const preloadStoryPlayer = importStoryPlayer
export const preloadStore = importStore
export const preloadSettings = importSettings
export const preloadStreaks = importStreaks

const Home = lazy(importHome)
const Explore = lazy(importExplore)
const Settings = lazy(importSettings)
const StoryPlayer = lazy(importStoryPlayer)
const Store = lazy(importStore)
const Streaks = lazy(importStreaks)
const Stickers = lazy(importStickers)
const ProfileLook = lazy(importProfileLook)
const ProfileSettings = lazy(importProfileSettings)
const Notifications = lazy(importNotifications)
const Preferences = lazy(importPreferences)
const Legal = lazy(importLegal)
const Privacy = lazy(importPrivacy)
const Terms = lazy(importTerms)
const ChildrenPrivacy = lazy(importChildrenPrivacy)
// Dev-only design surfaces — only bundled and routed when running
// the local dev server. Vite tree-shakes the `false && …` branches
// in prod builds, so the chunks never reach moonpath-kids.pages.dev.
const DevEndings = import.meta.env.DEV ? lazy(() => import('./pages/DevEndings.jsx')) : null
const DevParentGate = import.meta.env.DEV ? lazy(() => import('./pages/DevParentGate.jsx')) : null

// Each tab carries its own hue. Inactive icon paints in that color;
// active state turns the whole tab button into a solid color block
// with a white icon, matching the kids-app "tap a primary color"
// pattern.
const tabs = [
  { id: 'home',    label: 'Home',    icon: 'home',   color: 'var(--color-mp-blue-500)',   edge: 'var(--color-mp-blue-700)' },
  { id: 'explore', label: 'Explore', icon: 'search', color: 'var(--color-mp-green-500)',  edge: 'var(--color-mp-green-700)' },
  { id: 'stickers', label: 'Stickers', icon: 'star',  color: 'var(--color-mp-orange-500)', edge: 'var(--color-mp-orange-700)' },
  // Profile tab is special — its visual is the user's avatar disk
  // (colored emoji), so the indicator stays neutral when active so
  // the avatar's own colors aren't overridden.
  { id: 'profile', label: 'Profile', icon: 'user',   color: 'oklch(0.95 0.005 270)',      edge: 'oklch(0.85 0.005 270)' },
]

function LoadingScreen() {
  return (
    <div
      className="fixed inset-0 bg-[var(--inv-bg)] overflow-hidden"
      aria-label="Loading"
      role="status"
    >
      <div className="absolute inset-0 video-shimmer opacity-30" aria-hidden="true" />
    </div>
  )
}

const PAYWALL_SEEN_KEY = 'mp-paywall-seen'
const FAKE_SUB_KEY = 'mp-fake-subscribed'

function AppShell() {
  const location = useLocation()
  const navigate = useNavigate()

  const [paywallOpen, setPaywallOpen] = useState(false)

  // First-launch device registration. Pushes the FCM token (when push
  // permission is granted) and pulls the is_dev flag down into the
  // shared store. Idempotent — safe to call on every launch.
  useEffect(() => {
    registerDevice().catch(() => {})
    // No auto push-permission prompt — Apple Kids requires a
    // parent-aware screen first. Permission is requested only
    // when the user enables push from the Notifications settings
    // page (gated by ParentalGate).

    // Heartbeat ping on visibility change so admin grant/revoke of
    // is_dev takes effect within one app foreground.
    const onVis = () => { if (!document.hidden) pingDevice().catch(() => {}) }
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [])

  // First-launch paywall — shows once per install.
  useEffect(() => {
    try {
      if (!localStorage.getItem(PAYWALL_SEEN_KEY)) setPaywallOpen(true)
    } catch {}
  }, [])

  const dismissPaywall = (subscribedPlan = null) => {
    try {
      localStorage.setItem(PAYWALL_SEEN_KEY, '1')
      if (subscribedPlan) {
        localStorage.setItem(FAKE_SUB_KEY, JSON.stringify({ plan: subscribedPlan, ts: Date.now() }))
      }
    } catch {}
    setPaywallOpen(false)
  }

  const hideNav = location.pathname.startsWith('/play') || location.pathname.startsWith('/dev/') || location.pathname === '/store' || location.pathname === '/streaks' || location.pathname === '/privacy' || location.pathname === '/terms' || location.pathname === '/children-privacy' || location.pathname === '/profile/look' || location.pathname.startsWith('/settings/')

  const tabRoutes = { '/': 'home', '/explore': 'explore', '/stickers': 'stickers', '/settings': 'profile' }
  const activeTab = tabRoutes[location.pathname] || 'home'

  const handleTabChange = (tabId) => {
    if (tabId === 'home') navigate('/')
    if (tabId === 'explore') navigate('/explore')
    if (tabId === 'stickers') navigate('/stickers')
    if (tabId === 'profile') navigate('/settings')
  }

  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes location={location}>
        <Route path="/" element={<Home />} />
        <Route path="/explore" element={<Explore />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/store" element={<Store />} />
        <Route path="/streaks" element={<Streaks />} />
        <Route path="/stickers" element={<Stickers />} />
        <Route path="/badges" element={<Stickers />} />
        <Route path="/profile/look" element={<ProfileLook />} />
        <Route path="/settings/profile" element={<ProfileSettings />} />
        <Route path="/settings/notifications" element={<Notifications />} />
        <Route path="/settings/preferences" element={<Preferences />} />
        <Route path="/settings/legal" element={<Legal />} />
        <Route path="/play/:storyId?" element={<StoryPlayer />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/children-privacy" element={<ChildrenPrivacy />} />
        {import.meta.env.DEV && DevEndings && <Route path="/dev/endings" element={<DevEndings />} />}
        {import.meta.env.DEV && DevParentGate && <Route path="/dev/parent-gate" element={<DevParentGate />} />}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {!hideNav && (
        <BottomNav
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
      )}

      {paywallOpen && (
        <Paywall
          onDismiss={() => dismissPaywall()}
          onSubscribe={(plan) => dismissPaywall(plan)}
        />
      )}
    </Suspense>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </ErrorBoundary>
  )
}
